import { useEffect, useRef } from 'react';
import type * as LeafletNS from 'leaflet';
import { useTheme } from '../../../shared/theme/ThemeProvider';

export interface MapPlace {
  kind: 'sede' | 'oficina' | 'casa';
  name: string;
  lat: number;
  lon: number;
  radius: number;
  /** PENDIENTE se dibuja punteado. */
  status?: 'APROBADA' | 'PENDIENTE' | 'RECHAZADA' | null;
}
export interface MapUser {
  lat: number;
  lon: number;
  accuracy: number | null;
  /** true = dentro de un lugar autorizado (verde); false = fuera (naranja); null = sin referencia (azul). */
  inside: boolean | null;
}

const PLACE_COLOR: Record<MapPlace['kind'], string> = { sede: '#2563eb', oficina: '#2563eb', casa: '#7c3aed' };
const KIND_LABEL: Record<MapPlace['kind'], string> = { sede: 'Sede', oficina: 'Oficina', casa: 'Remoto' };

const pinHtml = (color: string, pulse: boolean) =>
  `<div style="position:relative;width:22px;height:22px"><span style="position:absolute;inset:0;border-radius:9999px;background:${color};opacity:.35;${pulse ? 'animation:lcc-pulse 1.8s ease-out infinite;' : ''}"></span><span style="position:absolute;inset:4px;border-radius:9999px;background:${color};border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.45)"></span></div>`;
const flagHtml = (color: string) =>
  `<div style="width:18px;height:18px;border-radius:6px;background:${color};border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.4)"></div>`;

/**
 * Mapa Leaflet con teselas CARTO (oscuras en los temas oscuro/WMS, claras en los demás) sobre OpenStreetMap:
 * gratis y sin llave. Dibuja los lugares autorizados con su círculo de tolerancia y, si se da, la posición
 * de la persona con su margen de error (±m). Con onPick, tocar el mapa devuelve las coordenadas.
 * Leaflet se carga solo cuando se abre un mapa (no pesa en el resto de la app).
 */
export function LeafletMap({ places, user, onPick, pick, className = '', fitKey }: { places: MapPlace[]; user?: MapUser | null; onPick?: (lat: number, lon: number) => void; pick?: { lat: number; lon: number } | null; className?: string; fitKey?: string }) {
  const { theme } = useTheme();
  const dark = theme === 'oscuro' || theme === 'wms';
  const host = useRef<HTMLDivElement>(null);
  const state = useRef<{ L: typeof LeafletNS; map: LeafletNS.Map; tiles: LeafletNS.TileLayer; layers: LeafletNS.LayerGroup; fitted: string | null } | null>(null);
  const latest = useRef({ places, user, pick, onPick, fitKey });
  latest.current = { places, user, pick, onPick, fitKey };

  const draw = () => {
    const s = state.current;
    if (!s) return;
    const { L, map, layers } = s;
    const { places: ps, user: u, pick: pk, fitKey: fk } = latest.current;
    layers.clearLayers();
    const bounds: [number, number][] = [];
    for (const p of ps) {
      const color = PLACE_COLOR[p.kind];
      L.circle([p.lat, p.lon], { radius: p.radius, color, weight: 2, fillColor: color, fillOpacity: 0.14, dashArray: p.status === 'PENDIENTE' ? '6 6' : undefined }).addTo(layers);
      L.marker([p.lat, p.lon], { icon: L.divIcon({ html: flagHtml(color), className: '', iconSize: [18, 18], iconAnchor: [9, 9] }) })
        .bindTooltip(`${KIND_LABEL[p.kind]}: ${p.name} · radio ${Math.round(p.radius)} m${p.status === 'PENDIENTE' ? ' · pendiente de aprobación' : ''}`)
        .addTo(layers);
      bounds.push([p.lat, p.lon]);
    }
    if (pk) {
      L.marker([pk.lat, pk.lon], { icon: L.divIcon({ html: pinHtml('#ef4444', false), className: '', iconSize: [22, 22], iconAnchor: [11, 11] }) }).addTo(layers);
      bounds.push([pk.lat, pk.lon]);
    }
    if (u) {
      const color = u.inside === true ? '#16a34a' : u.inside === false ? '#f97316' : '#0ea5e9';
      if (u.accuracy && u.accuracy > 0) L.circle([u.lat, u.lon], { radius: u.accuracy, color, weight: 1.5, fillColor: color, fillOpacity: 0.18 }).addTo(layers);
      L.marker([u.lat, u.lon], { icon: L.divIcon({ html: pinHtml(color, true), className: '', iconSize: [22, 22], iconAnchor: [11, 11] }) }).bindTooltip('Tú estás aquí').addTo(layers);
      bounds.push([u.lat, u.lon]);
    }
    const key = fk ?? `${bounds.length}`;
    if (s.fitted !== key && bounds.length > 0) {
      s.fitted = key;
      if (bounds.length === 1) map.setView(bounds[0], 17);
      else map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
    }
  };

  // Crear el mapa una sola vez
  useEffect(() => {
    let cancelled = false;
    let ro: ResizeObserver | null = null;
    (async () => {
      const L = (await import('leaflet')).default as unknown as typeof LeafletNS;
      await import('leaflet/dist/leaflet.css');
      if (cancelled || !host.current) return;
      if (!document.getElementById('lcc-pulse-style')) {
        const st = document.createElement('style');
        st.id = 'lcc-pulse-style';
        st.textContent = '@keyframes lcc-pulse{0%{transform:scale(.6);opacity:.6}100%{transform:scale(2.4);opacity:0}}';
        document.head.appendChild(st);
      }
      const map = L.map(host.current, { zoomControl: true, attributionControl: true }).setView([6.2442, -75.5812], 14);
      const tiles = L.tileLayer(`https://{s}.basemaps.cartocdn.com/${dark ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png`, {
        subdomains: 'abcd',
        maxZoom: 20,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      }).addTo(map);
      const layers = L.layerGroup().addTo(map);
      state.current = { L, map, tiles, layers, fitted: null };
      map.on('click', (e: LeafletNS.LeafletMouseEvent) => latest.current.onPick?.(e.latlng.lat, e.latlng.lng));
      ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(host.current);
      draw();
    })();
    return () => {
      cancelled = true;
      ro?.disconnect();
      state.current?.map.remove();
      state.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redibujar al cambiar datos
  useEffect(draw, [places, user, pick, fitKey]);

  // Cambiar de tema = cambiar teselas
  useEffect(() => {
    const s = state.current;
    if (s) s.tiles.setUrl(`https://{s}.basemaps.cartocdn.com/${dark ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png`);
  }, [dark]);

  return <div ref={host} className={['z-0 overflow-hidden rounded-2xl bg-ink-100', onPick ? 'cursor-crosshair' : '', className].join(' ')} />;
}
